import { cacheUtils } from "../..";
import FeatureFlagService from "@services/featureFlagService/FeatureFlagService";
import * as Storage from "@services/storage/storeTemplate";
import { CacheTypes } from "../cache/interfaces";
import { requiredOptions as preFlag } from "../requiredOptions/index.current";
import { requiredOptions as postFlag } from "../requiredOptions/index.new";
import { JobTypesAnswer, InputTypes, JobTypesOption } from "../requiredOptions/interfaces";
import Version from "../version";
import { state, ui } from "./../../interfaces/interfaces";

// QuestionsUtils class loading questions and validating answers

// dynamic import based on the environment
const featureFlag = {
  featureName: "SimilarJobCross-links",
  applicationId: Version.getApplicationId(),
}

let similarJobCrossLinksEnabled = false;

FeatureFlagService.subscribe(featureFlag.applicationId, "Questions", () => {
  similarJobCrossLinksEnabled = FeatureFlagService.isFeatureEnabled({ applicationId: featureFlag.applicationId, featureName: "SimilarJobCross-links" })
  console.log("FeatureFlagService.subscribe", similarJobCrossLinksEnabled);
});

interface AnswersImport {
  answers: { answer: string; required?: boolean }[];
  jobTypeCode: string;
}

export interface QuestionObject {
  question: string;
  answer: string;
  required: boolean;
  dependent: boolean;
  dependency: number;
  valid: boolean;
  options: JobTypesAnswer[];
  trigger: string | undefined;
  type: InputTypes;
  id: number;
  altJobTypes?: string[];
}

export class QuestionsUtils {
  private questions: { [key: number]: Question } = {};
  private valid: boolean = false;
  private jobTypeCode: string = "";

  constructor() {
    this.jobTypeCode = "";
    this.questions = {};
  }

  print(): string {
    const jobType = cacheUtils
      .getCache<CacheTypes.JobTypes>("JobTypes")
      .find((jobType) => jobType.jobTypeCode === this.jobTypeCode);
    let questions = [];
    let text = "";
    text += `${this.jobTypeCode}|${jobType?.tradeCode}|${jobType?.jobGroupCode}|${jobType?.jobDescription}|`;
    Object.values(this.getQuestions()).forEach((question, idx) => {
      questions.push(question.getQuestion());
      text += `${question.getType()}|${question.getDependency() ?? "Independent"}|${question.getTrigger() ?? "No Trigger"
        }|`;
      text += `${question.getQuestion()}|`;
      const options = question.getOptions();
      if (options) {
        text += `${options.map((option) => option.text).join("/")}|`;
      }
    });
    return text;
  }

  // special function to export questions which can easily be imported into excel
  printAllQuestions(): void {
    let allQuestions = "";
    const jobTypes = cacheUtils.getCache<CacheTypes.JobTypes>("JobTypes");
    jobTypes.forEach((jobType) => {
      this.loadQuestions(jobType.jobTypeCode);
      allQuestions += this.print() + "\n";
    });
    console.log(allQuestions);
  }

  // load questions for the tradeCode
  loadQuestions(jobTypeCode?: string) {
    // if jobTypeCode is provided, use it
    if (jobTypeCode) {
      // check if the jobTypeCode is different from the current jobTypeCode
      if (jobTypeCode !== this.jobTypeCode) {
        this.jobTypeCode = jobTypeCode;
        // remove all the questions
        this.removeQuestionsButExtra();
        const requiredOptions = similarJobCrossLinksEnabled ? postFlag : preFlag;

        const questions = requiredOptions[this.jobTypeCode];
        if (questions) {
          // counter to keep track of the question number
          let counter = 0;
          for (const question of questions) {
            this.questions[counter] = new Question(question, counter as AnswersNumber);
            counter++;
          }
        }
      }
    }
    this.checkValidity();
  }

  importQuestionsAnswers(answersImport: AnswersImport, answer10: string): void {
    this.loadQuestions(answersImport.jobTypeCode);

    answersImport.answers.forEach((answer, idx) => {
      this.questions[idx].setAnswer(answer.answer);
      answer?.required && this.questions[idx].setRequired(answer.required);
    });
    if (answer10) {
      state[`RequiredOption10Valid`] = true;
      state[`RequiredOption10Answer`] = answer10;
    }
    this.checkValidity();

    // if answer is No, and jobTypeCode is "HRLEK" and question is first question, we need CADENT message
    if (answersImport.jobTypeCode === "HRLEK" && answersImport.answers[0].answer === "No") {
      // this.ui[`RequiredOption${questionNumber + 1}Message`] = "Please select a valid answer";
      ui.CadentModal = true;
    }
  }

  //remove all the questions
  removeQuestions(): void {
    this.questions = {};
    // iterate from 0 to 10
    for (let i = 0; i < 10; i++) {
      state[`RequiredOption${(i + 1) as AnswersNumber}Valid`] = false;
      state[`RequiredOption${(i + 1) as AnswersNumber}Answer`] = "";
    }
  }

  removeQuestionsButExtra(): void {
    this.questions = {};
    // iterate from 0 to 9
    for (let i = 0; i < 9; i++) {
      state[`RequiredOption${(i + 1) as AnswersNumber}Valid`] = false;
      state[`RequiredOption${(i + 1) as AnswersNumber}Answer`] = "";
    }
  }

  // reset should remove all questions and set valid to false, set jobTypeCode to ""
  reset(): void {
    this.removeQuestions();
    this.jobTypeCode = "";
    this.valid = false;
  }

  // isValid method to check if the answers are valid
  isValid(): boolean {
    return this.valid;
  }

  // set isValid method to set the valid state
  setValid(valid: boolean): void {
    this.valid = valid;
  }

  // getQuestions method to get the questions
  getQuestions(): { [key: number]: Question } {
    return this.questions;
  }

  // getAnswer method to get the answer for the question
  getAnswer(questionNumber: number): string {
    return this.questions[questionNumber].getAnswer();
  }

  //setAnswer method to set the answer
  setAnswer(questionNumber: number, answer: string): void {
    const question = this.questions[questionNumber];
    const isPositiveAnswer = question.setAnswer(answer);

    // if answer is No, and jobTypeCode is "HRLEK" and question is first question, we need CADENT message
    if (this.jobTypeCode === "HRLEK" && questionNumber === 0 && answer === "No") {
      // this.ui[`RequiredOption${questionNumber + 1}Message`] = "Please select a valid answer";
      ui.CadentModal = true;
    }

    // check if this question is not the last question and if the next question is dependent on the answer and if the answer is positive or matching trigger
    if (
      !this.isFinalQuestion(questionNumber) &&
      this.doesNextQuestionDependOnAnswer(questionNumber) &&
      this.isDependentQuestionRequired(questionNumber)
    ) {
      // set dependent question as required if answer was positive or matching trigger
      this.setNextQuestionRequiredStatus(
        questionNumber,
        isPositiveAnswer || this.isAnswerMatchingTrigger(questionNumber)
      );
    } else {
      // set dependent question as not required if answer was negative and not matching trigger ONLY IF it is not the last question
      !this.isFinalQuestion(questionNumber) && this.setNextQuestionRequiredStatus(questionNumber, false);
    }
    // check validity
    this.checkValidity();
  }

  isFinalQuestion(questionNumber: number): boolean {
    return questionNumber === Object.keys(this.questions).length - 1;
  }

  doesNextQuestionDependOnAnswer(questionNumber: number): boolean {
    return this.questions[questionNumber + 1].isDependent();
  }

  getDependentQuestion(questionNumber: number): Question {
    return this.questions[questionNumber + 1];
  }

  setNextQuestionRequiredStatus(questionNumber: number, required: boolean): void {
    this.questions[questionNumber + 1].setRequired(required);
  }

  isAnswerMatchingTrigger(questionNumber: number): boolean {
    const dependentQuestion = this.getDependentQuestion(questionNumber);

    const trigger = dependentQuestion.getTrigger();
    // if trigger contains |, it means that it is a list of triggers
    if (trigger && trigger.includes("|")) {
      // split the trigger by | and check if the answer is matching any of the triggers
      return trigger.split("|").some((trigger) => this.getAnswer(questionNumber) === trigger);
    }
    return this.getAnswer(questionNumber) === trigger;
  }

  /**
   * Check if the next question is required or not
   * @param questionNumber {number} - the question number to check if the next question is required
   * @returns  {boolean} - true if the question is required and valid or if the answer is matching the trigger for the next question
   */
  isDependentQuestionRequired(questionNumber: number): boolean {
    return this.questions[questionNumber].isValid() || this.isAnswerMatchingTrigger(questionNumber);
  }

  // checkValidity method to check if the answers are valid
  checkValidity(): boolean {
    // check if the questions are valid
    for (const question of Object.values(this.questions)) {
      // check if the question is dependent and required
      if (question.isDependent() && question.isRequired()) {
        // if the question is not valid, set the valid state to false
        if (!question.isValid()) {
          this.valid = false;
          state.RequiredOptionsValid = this.valid;
          return false;
        }
        // if the question is valid, check if the next question is valid
        else {
          this.valid = true;
        }
        // otherwise if the questions is not dependent but valid
      } else if (!question.isDependent() && question.isValid()) {
        this.valid = true;
      } else if (!question.isRequired() && question.isDependent()) {
        continue;
      } else {
        // set the valid state to false
        this.valid = false;
        state.RequiredOptionsValid = this.valid;
        return false;
      }
    }
    state.RequiredOptionsValid = this.valid;
    return this.valid;
  }

  // generate notes from questions and answers if valid
  generateNotes(): string {
    let notes = "\n";
    this.checkValidity();
    if (this.valid) {
      for (const question of Object.values(this.questions)) {
        // is not dependent or dependent but required
        if (!question.isDependent() || (question.isDependent() && question.isRequired()))
          notes += `* ${question.getQuestion()}:   ${question.getAnswer()}\n`;
      }
    }
    return notes;
  }

  // generate questions for form
  generateQuestions(): QuestionObject[] {
    const questions = [];
    for (const question of Object.values(this.questions)) {
      const questionObj = {
        question: question.getQuestion(),
        answer: question.getAnswer(),
        required: question.isRequired(),
        dependent: question.isDependent(),
        dependency: question.getDependency(),
        altJobTypes: question.getAltJobTypes(),
        valid: question.isValid(),
        options: question.getOptions(),
        trigger: question.getTrigger(),
        type: question.getType(),
        id: question.getId(),
      };
      questions.push(questionObj);
    }
    return questions;
  }
}

type AnswersNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

class Question {
  public id: AnswersNumber;
  public name: string;
  public question: string;
  public answer: string = "";
  public valid: boolean = false;
  public type: InputTypes;
  public options?: Array<JobTypesAnswer>; // setOptions method to set the options;
  public dependent?: boolean = false;
  public dependency?: number = 0;
  public trigger?: string = "";
  public required?: boolean = false;
  public altJobTypes?: string[] = [];
  // public value?: boolean = false;
  private state: Storage.TemplateSTATEObject;

  constructor(question: JobTypesOption, id: AnswersNumber) {
    this.id = id;
    this.name = question.name;
    this.question = question.question;
    this.type = question.type;
    this.options = question.answers;
    this.dependent = question.depends !== null ? true : false;
    this.dependency = this.dependent ? Number(question.depends) : undefined;
    this.altJobTypes = question.altJobTypes;
    this.trigger = question.trigger;

    this.state = state;

    if (this.state[`RequiredOption${(this.id + 1) as AnswersNumber}Answer`]) {
      // if there is an answer in the state
      this.answer = this.state[`RequiredOption${(this.id + 1) as AnswersNumber}Answer`]; // set the answer
    }
  }

  // isValid method to check if the answer is valid
  isValid(): boolean {
    return this.valid;
  }

  // set isValid method to set the valid state
  setValid(valid: boolean): void {
    this.valid = valid;
  }

  // get trigger
  getTrigger(): string | undefined {
    return this.trigger;
  }

  // getQuestion method to get the question
  getQuestion(): string {
    return this.question;
  }

  // getAnswer method to get the answer
  getAnswer(): string {
    return this.answer;
  }

  // getOptions method to get the options
  getOptions(): Array<JobTypesAnswer> {
    return this.options || [];
  }

  // getDependent method to get the dependent state
  isDependent(): boolean {
    return this.dependent || false;
  }

  // getDependency method to get the dependency
  getDependency(): number {
    return this.dependency || 0;
  }

  getAltJobTypes(): string[] | undefined {
    return this.altJobTypes;
  }

  logEverything(): void {
    // console log everything
    console.log(
      `Question: ${this.question}\n`,
      `Dependent: ${this.dependent}\n`,
      `Dependency: ${this.dependency}\n`,
      `Required: ${this.required}\n`,
      `Has Trigger: ${this.trigger !== undefined ? true : false}\n`,
      `Trigger: ${this.trigger}\n`,
      `Valid: ${this.valid}\n`,
      // `Value: ${this.value}\n`,
      `Answer: ${this.answer}\n`,
      `Options: ${this.options?.map((o) => o.text).join(", ") || ""} `
    );
  }

  // setAnswer method to set the answer
  setAnswer(answer: string): boolean {
    this.answer = answer; // set the answer
    this.state[`RequiredOption${(this.id + 1) as AnswersNumber}Answer`] = answer; // set the answer in the state
    const value = this.options?.find((o) => o.text === this.answer)?.value; // get the value of the answer
    // if type is text answer needs to be longer than 1 character or be a valid number
    if (this.type === "text" && (this.answer?.length > 1 || parseInt(this.answer) > 0)) {
      // if the type is text and the answer is longer than 1 character
      this.valid = true; // set the valid state to true
      this.state[`RequiredOption${(this.id + 1) as AnswersNumber}Valid`] = true; // set the valid state in the state
      return true; // return true
    }
    if (this.type === "radio") {
      // if the type is radio
      this.valid =
        (this.options?.find((o) => o.text === this.answer) !== undefined && this.answer !== "Other") ||
        (this.answer.length > 2 && this.answer !== "Other"); // check if the answer is in the options or is not empty
      this.state[`RequiredOption${(this.id + 1) as AnswersNumber}Valid`] = true; // set the valid state in the state
      const result =
        // check if the answer is in the options and the value is true or matches trigger
        this.valid && value && value === "true" ? true : false; // set the value

      // handle situation when the answer is not valid, but question is ""

      return result; // return the value
    }
    if (this.type === "checkbox") {
      // if the type is checkbox
      this.valid = this.options?.find((o) => o.text === this.answer) !== undefined; // check if the answer is in the options
      this.state[`RequiredOption${(this.id + 1) as AnswersNumber}Valid`] = true; // set the valid state in the state
      return this.valid && this.options?.find((o) => o.text === this.answer)?.value === "true"; // return the value
    }
    if (this.type === "message") {
      // if the type is message (no answer) we need to display popup
      this.valid = true;
      this.state[`RequiredOption${(this.id + 1) as AnswersNumber}Valid`] = true;
      ui.GenericPopupModal = true;
      return true;
    }
    this.valid = false; // set the valid state to false
    this.state[`RequiredOption${(this.id + 1) as AnswersNumber}Valid`] = false; // set the valid state in the state
    return false; // return false
  }

  // setOptions method to set the options
  setOptions(options: Array<JobTypesAnswer>): void {
    this.options = options;
  }

  // setDependent method to set the dependent state
  setDependent(dependent: boolean): void {
    this.dependent = dependent;
  }

  // setDependency method to set the dependency
  setDependency(dependency: number): void {
    this.dependency = dependency;
  }

  // getId method to get the id
  getId(): number {
    return this.id;
  }

  // getName method to get the name
  getName(): string {
    return this.name;
  }

  // getType method to get the type
  getType(): InputTypes {
    return this.type;
  }

  // setType method to set the type
  setType(type: InputTypes): void {
    this.type = type;
  }

  // isRequired method to check if the question is required
  isRequired(): boolean {
    return this.required || false;
  }

  // setRequired method to set the required state
  setRequired(required: boolean): void {
    if (this.required !== required) {
      this.required = required;
      this.state.RequiredOptionsChanged = true;
    }
  }
}

// declare global variable QuestionUtils
declare global {
  interface Window {
    QuestionUtils: QuestionsUtils;
  }
}

window.QuestionUtils = new QuestionsUtils(); // initialize the global variable QuestionUtils

export default QuestionsUtils;
